import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { AuthLayout } from '@/components/layout/auth-layout'
import { Button } from '@/components/primitives/button'
import { AuthBrandHeader } from '@/features/auth/components/brand-header'
import { LanguageSegmentedControl } from '@/features/auth/components/language-segmented-control'
import { consumeOAuthAuthorizationReturnUrl } from '@/features/auth/oauth/authorization-resume'
import { exchangeUserSsoCode } from '@/features/auth/sso/user-api'
import { normalizeLanguage, setAppLanguage } from '@/i18n/i18n'
import { clearPortalSession, persistPortalSession } from '@/lib/auth/portal-session'
import { portalDefaultPath, portalPasswordResetRoutePath } from '@/lib/auth/portal-routes'
import {
  appendUserLoginResumeMarker,
  consumeUserPostLoginReturnPath,
} from '@/lib/auth/session-storage'

function readHandoffCode() {
  return new URLSearchParams(window.location.search).get('code')
}

function readCallbackErrorCode() {
  return new URLSearchParams(window.location.search).get('error_code')
}

export function SsoCallbackPage() {
  const { i18n, t } = useTranslation('auth')
  const { showFeedback } = useFeedback()
  const language = normalizeLanguage(i18n.language)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const attemptedRef = useRef(false)
  const code = readHandoffCode()
  const callbackErrorCode = readCallbackErrorCode()
  const displayedErrorMessage = callbackErrorCode
    ? t('ssoCallback.errorDescription')
    : code
      ? errorMessage
      : t('ssoCallback.missingCode')
  const exchangeMutation = useMutation({
    mutationFn: exchangeUserSsoCode,
    onError: () => {
      clearPortalSession('user')
      setErrorMessage(t('ssoCallback.errorDescription'))
    },
    onSuccess: (response) => {
      persistPortalSession('user', response.data, { source: 'sso' })
      showFeedback({
        message: t('ssoCallback.success'),
        tone: 'success',
      })

      window.location.replace(
        response.data.password_reset_required
          ? `${portalPasswordResetRoutePath.user}?login_entry=password`
          : (consumeOAuthAuthorizationReturnUrl() ??
              consumeUserPostLoginReturnPath() ??
              portalDefaultPath.user),
      )
    },
  })

  useEffect(() => {
    if (attemptedRef.current) {
      return
    }
    attemptedRef.current = true

    if (!code || callbackErrorCode) {
      clearPortalSession('user')
      return
    }

    exchangeMutation.mutate(code)
  }, [callbackErrorCode, code, exchangeMutation, t])

  return (
    <AuthLayout
      actions={
        <LanguageSegmentedControl
          language={language}
          onLanguageChange={(nextLanguage) => {
            void setAppLanguage(nextLanguage)
          }}
          t={t}
        />
      }
      cardClassName="min-h-[420px]"
      portal="user"
    >
      <div className="grid min-h-[300px] content-center gap-6">
        <AuthBrandHeader title={t('user.title')} />
        {displayedErrorMessage ? (
          <div className="grid gap-5">
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-4 py-3 text-sm leading-6 text-[var(--color-danger)]"
            >
              <p className="font-semibold">{t('ssoCallback.errorTitle')}</p>
              <p className="mt-1">{displayedErrorMessage}</p>
            </div>
            <Button
              className="h-[42px] rounded-lg text-base font-bold"
              onClick={() => {
                window.location.assign(appendUserLoginResumeMarker('/login/password'))
              }}
              type="button"
            >
              {t('ssoCallback.backToLogin')}
            </Button>
          </div>
        ) : (
          <div className="grid justify-items-center gap-4 text-center">
            <div className="size-10 animate-spin rounded-full border-2 border-[rgba(25,91,255,0.16)] border-t-[var(--color-primary)]" />
            <p className="text-sm leading-6 text-[var(--color-text-muted)]">
              {t('ssoCallback.processing')}
            </p>
          </div>
        )}
      </div>
    </AuthLayout>
  )
}
